import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useEffect, useState } from 'react'
import ECharts from '~/components/echarts'
import {
  awsSummarySavingsOrganizationOptimizerAWSMasterOptimizationsPath,
  ec2InstanceTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath,
  ec2OptimizedInstanceTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath,
  ec2SavingsPlansChartOrganizationOptimizerAWSMasterOptimizationsPath,
  ec2SummaryChartOrganizationOptimizerAWSMasterOptimizationsPath,
} from '~/routes'
import { csrfHeader } from '~/utils/csrf'

export default function EC2Optimization({ organizationId, masterOptimizationId, masterOptimization }) {
  const [activeTab, setActiveTab] = useState('daily-cost')
  const [savingsData, setSavingsData] = useState(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  useEffect(() => {
    if (masterOptimization) {
      fetchSavingsData()
    }
  }, [masterOptimization])

  const fetchSavingsData = async () => {
    try {
      const savingsUrl = awsSummarySavingsOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)
      const response = await fetch(savingsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...csrfHeader(),
        },
        body: JSON.stringify({ master_optimization_id: masterOptimizationId }),
      })

      if (response.ok) {
        const data = await response.json()
        setSavingsData(data)
      } else {
        console.error('Failed to fetch EC2 savings data:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('Error fetching EC2 savings data:', error)
    }
  }

  if (!masterOptimization) {
    return null
  }

  const handleTabChange = (tabId) => {
    setActiveTab(tabId)
  }

  const dailySavings = savingsData?.ec2_savings?.daily_savings || 0
  const savingsPercentage = savingsData?.ec2_savings?.savings_percentage || 0
  const hasInstanceTypeChanges = (savingsData?.ec2_savings?.instance_type_changes_count || 0) > 0

  return (
    <div className='row mb-4'>
      <div className='col-12'>
        <div className='card'>
          <div className='card-header'>
            <h5 className='card-title mb-0'>
              <Trans>
                EC2 Optimization - Daily Savings ${dailySavings.toFixed(2)} ({savingsPercentage.toFixed(1)}%)
              </Trans>
            </h5>
          </div>
          <div className='card-body'>
            <ul className='nav nav-tabs mb-3'>
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'daily-cost' ? 'active' : ''}`}
                  onClick={() => handleTabChange('daily-cost')}
                  type='button'
                >
                  <Trans>Daily Cost</Trans>
                </button>
              </li>
              {hasInstanceTypeChanges && (
                <li className='nav-item'>
                  <button
                    className={`nav-link ${activeTab === 'ec2-instance' ? 'active' : ''}`}
                    onClick={() => handleTabChange('ec2-instance')}
                    type='button'
                  >
                    <Trans>EC2 Instance</Trans>
                  </button>
                </li>
              )}
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'savings-plans' ? 'active' : ''}`}
                  onClick={() => handleTabChange('savings-plans')}
                  type='button'
                >
                  <Trans>Savings Plans Management</Trans>
                </button>
              </li>
            </ul>

            <div className='mt-3'>
              {activeTab === 'daily-cost' && (
                <ECharts
                  title={t`Daily Cost Comparison`}
                  chartInfoTitle='aws_ec2_optimization_daily_cost_comparison'
                  url={ec2SummaryChartOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                  params={{ master_optimization_id: masterOptimizationId }}
                />
              )}

              {hasInstanceTypeChanges && activeTab === 'ec2-instance' && (
                <div className='row'>
                  <div className='col-md-6'>
                    <ECharts
                      title={t`Current Types of Changed Instances`}
                      chartInfoTitle='aws_ec2_current_instance_types'
                      url={ec2InstanceTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                      params={{ master_optimization_id: masterOptimizationId }}
                    />
                  </div>
                  <div className='col-md-6'>
                    <ECharts
                      title={t`Recommended Instance Types`}
                      chartInfoTitle='aws_ec2_optimized_instance_types'
                      url={ec2OptimizedInstanceTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath(
                        organizationId,
                      )}
                      params={{ master_optimization_id: masterOptimizationId }}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'savings-plans' && (
                <ECharts
                  title={t`Savings Plans Management`}
                  chartInfoTitle='aws_ec2_savings_plans_management_overview'
                  url={ec2SavingsPlansChartOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                  params={{ master_optimization_id: masterOptimizationId }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
